import { useState } from "react";
import { Link } from "react-router-dom";
import {
  SlidersHorizontal, Ruler, TriangleRight, Home, Download, ArrowRight, Info, Droplets,
} from "lucide-react";
import Seo from "@/components/Seo";
import RoofDiagram from "@/components/RoofDiagram";
import { useAuth } from "@/context/AuthContext";

// Representative sample (illustrative roof measurement values).
const SAMPLE = {
  address: "742 Evergreen Terrace, Springfield, IL 62704",
  totalArea: 3056,
  totalSquares: 30.6,
  predominantPitch: "6/12",
  pitchComplexity: "Medium",
  distinctPitchCount: 2,
  facets: 10,
  pitchBreakdown: [
    { pitch: "6/12", area: 2140, squares: 21.4, percent: 70 },
    { pitch: "8/12", area: 916, squares: 9.2, percent: 30 },
  ],
  edges: { ridgesFt: 78, hipsFt: 44, valleysFt: 32, rakesFt: 88, eavesFt: 96, flashingFt: 18, stepFlashingFt: 22, parapetsFt: 0 },
  gutter: { downspouts: 7, storiesMax: 2, miterInside: 4, miterOutside: 6 },
};

export default function SampleReport() {
  const { user } = useAuth();
  const authed = user && typeof user === "object";
  const [waste, setWaste] = useState(10);
  const adjusted = +(SAMPLE.totalSquares * (1 + waste / 100)).toFixed(1);

  return (
    <div className="container-mtr py-10 sm:py-14 max-w-4xl" data-testid="sample-report-page">
      <Seo
        title="Sample Roof Measurement Report — MeasureTheRoof"
        description="See a real example of a MeasureTheRoof aerial roof report: total squares, pitch breakdown, ridge/hip/valley/eave lengths, facet count, gutters, and an interactive waste-factor calculator."
        path="/sample-report"
      />

      <span className="eyebrow">Sample report</span>
      <h1 className="font-body font-extrabold text-navy text-[30px] sm:text-[38px] mt-2">What you get in every report</h1>
      <p className="text-iron text-[16px] mt-3 max-w-2xl leading-relaxed">
        This is a representative MeasureTheRoof report. Every measurement below comes straight from
        high-resolution aerial imagery — total area, pitch, and the edge lengths you need to order
        materials. Drag the waste slider to see your "squares to order" update live.
      </p>
      <div className="mt-4 inline-flex items-center gap-2 text-[13px] text-mortar bg-white rounded-input px-3 py-2 border border-gravel">
        <Info size={15} className="text-orange" /> Sample data for illustration. Your report shows the actual property you order.
      </div>

      {/* Headline visual + adjusted squares */}
      <div className="grid lg:grid-cols-2 gap-5 mt-8">
        <div className="card-mtr">
          <p className="label-mtr">Property</p>
          <p className="font-semibold text-iron text-[15px] mb-3">{SAMPLE.address}</p>
          <RoofDiagram className="w-full h-52 rounded-input" />
          <p className="text-[12px] text-mortar mt-2">Orange = ridges &amp; hips · Green = valleys · labels show measured lengths.</p>
        </div>
        <div className="card-mtr flex flex-col">
          <span className="label-mtr flex items-center gap-2"><SlidersHorizontal size={14} className="text-orange" /> Waste factor <span className="text-orange ml-auto font-extrabold" data-testid="sample-waste-value">{waste}%</span></span>
          <input type="range" min="5" max="25" value={waste} onChange={(e) => setWaste(+e.target.value)} className="w-full accent-orange mt-2" data-testid="sample-waste-slider" aria-label="Waste factor" />
          <div className="flex justify-between text-[12px] text-mortar mt-1"><span>5%</span><span>25%</span></div>
          <div className="bg-fieldstone rounded-input p-5 text-center mt-4 flex-1 flex flex-col justify-center">
            <p className="label-mtr !mb-1">Squares to order</p>
            <p className="metric text-[48px] leading-none text-orange" data-testid="sample-adjusted">{adjusted}</p>
            <p className="text-[13px] text-mortar mt-2">{SAMPLE.totalSquares} squares + {waste}% waste</p>
          </div>
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
        <Metric icon={Home} label="Total area" value={SAMPLE.totalArea.toLocaleString()} unit="sq ft" />
        <Metric icon={Ruler} label="Total squares" value={SAMPLE.totalSquares} />
        <Metric icon={TriangleRight} label="Predominant pitch" value={SAMPLE.predominantPitch} />
        <Metric icon={SlidersHorizontal} label="Pitch complexity" value={SAMPLE.pitchComplexity} />
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-5">
        <div className="card-mtr">
          <h2 className="font-bold text-navy text-[17px] mb-4">Edge lengths</h2>
          <dl className="grid grid-cols-2 gap-y-3 gap-x-4">
            <Edge label="Ridges" value={SAMPLE.edges.ridgesFt} />
            <Edge label="Hips" value={SAMPLE.edges.hipsFt} />
            <Edge label="Valleys" value={SAMPLE.edges.valleysFt} />
            <Edge label="Rakes" value={SAMPLE.edges.rakesFt} />
            <Edge label="Eaves" value={SAMPLE.edges.eavesFt} />
            <Edge label="Flashing" value={SAMPLE.edges.flashingFt} />
            <Edge label="Step flashing" value={SAMPLE.edges.stepFlashingFt} />
            <Edge label="Parapets" value={SAMPLE.edges.parapetsFt} />
          </dl>
        </div>
        <div className="space-y-5">
          <div className="card-mtr">
            <h2 className="font-bold text-navy text-[17px] mb-3">Pitch breakdown</h2>
            <table className="w-full text-[14px]">
              <thead>
                <tr className="text-mortar text-[12px] uppercase tracking-wide text-left">
                  <th className="font-semibold pb-2">Pitch</th><th className="font-semibold pb-2 text-right">Area</th><th className="font-semibold pb-2 text-right">%</th>
                </tr>
              </thead>
              <tbody className="tnum">
                {SAMPLE.pitchBreakdown.map((p, i) => (
                  <tr key={i} className="border-t border-gravel">
                    <td className="py-2 font-semibold text-navy">{p.pitch}</td>
                    <td className="py-2 text-right text-iron">{p.area.toLocaleString()} ft²</td>
                    <td className="py-2 text-right text-iron">{p.percent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card-mtr">
            <h2 className="font-bold text-navy text-[17px] mb-3 flex items-center gap-2"><Droplets size={18} className="text-sky-blue" /> Gutters &amp; structure</h2>
            <dl className="grid grid-cols-2 gap-y-3 gap-x-4">
              <Edge label="Downspouts (est.)" value={SAMPLE.gutter.downspouts} unit="" />
              <Edge label="Max stories" value={SAMPLE.gutter.storiesMax} unit="" />
              <Edge label="Inside miters" value={SAMPLE.gutter.miterInside} unit="" />
              <Edge label="Outside miters" value={SAMPLE.gutter.miterOutside} unit="" />
            </dl>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="card-mtr mt-8 bg-navy text-white text-center">
        <h2 className="font-body font-extrabold text-[22px]">Ready to run one for a real address?</h2>
        <p className="text-white/80 text-[14px] mt-2">Order a report in seconds — delivered to your inbox while you keep working.</p>
        <div className="flex flex-wrap gap-3 justify-center mt-5">
          <Link to={authed ? "/dashboard" : "/signup"} className="btn-primary" data-testid="sample-cta">
            {authed ? "Order a report" : "Get started"} <ArrowRight size={18} />
          </Link>
          <button className="btn-ghost !text-white !border-white/40 hover:!bg-white hover:!text-navy" disabled>
            <Download size={18} /> PDF included with every report
          </button>
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, unit }) {
  return (
    <div className="card-mtr !p-4">
      <Icon className="text-orange mb-2" size={20} aria-hidden="true" />
      <p className="text-[11px] uppercase tracking-[0.08em] text-mortar font-semibold">{label}</p>
      <p className="metric text-[24px] mt-0.5">{value}{unit && <span className="text-[13px] text-mortar font-normal ml-1">{unit}</span>}</p>
    </div>
  );
}

function Edge({ label, value, unit = "ft" }) {
  return (
    <div>
      <dt className="text-[12px] uppercase tracking-[0.06em] text-mortar font-semibold">{label}</dt>
      <dd className="metric text-[18px]">{value}{unit && <span className="text-[12px] text-mortar font-normal"> {unit}</span>}</dd>
    </div>
  );
}
